import { Component, EventEmitter, Output } from '@angular/core';
import { ToastService } from 'src/app/shared/services';
import { commonService } from 'src/app/shared/services/common.service';

@Component({
  selector: 'app-feedback-form',
  templateUrl: './feedback-form.component.html',
  styleUrls: ['./feedback-form.component.css'],
})
export class FeedbackFormComponent {
  rating = 0;
  hoverRating = 0;
  message = '';

  selectedCategory = '';

  selectedFile: File | null = null;
  selectedFileName = '';
  stars = [1, 2, 3, 4, 5];
  isSubmitting = false;
  isDragging = false;

  /** 10 MB, matching the hint shown in the dropzone. */
  private readonly maxFileSize = 10 * 1024 * 1024;
  private readonly acceptedTypes = ['image/png', 'image/jpeg', 'image/gif'];

  categories = [
    {
      label: '🐞 Bug Report',
      value: 'BUG',
    },
    {
      label: '✨ Feature Request',
      value: 'FEATURE',
    },
    {
      label: '💡 Suggestion',
      value: 'SUGGESTION',
    },
    {
      label: '📝 General Feedback',
      value: 'GENERAL',
    },
  ];

  @Output() closeModal = new EventEmitter<void>();

  constructor(
    private commonService: commonService,
    private toast: ToastService,
  ) {}

  /* ---------------------------- Rating ---------------------------- */

  setRating(value: number) {
    this.rating = value;
  }

  get ratingLabel(): string {
    const labels = ['Poor', 'Fair', 'Good', 'Great', 'Excellent'];
    return labels[this.rating - 1] || '';
  }

  /* ---------------------------- Closing --------------------------- */

  close() {
    this.resetForm();
    this.closeModal.emit();
  }

  onBackdrop(event: MouseEvent) {
    event.stopPropagation();
    this.close();
  }

  /* ------------------------- File handling ------------------------ */

  onFileSelected(event: any) {
    const file = event.target.files?.[0];

    if (file) {
      this.handleFile(file);
    }

    // Allow re-selecting the same file after a removal.
    event.target.value = '';
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const file = event.dataTransfer?.files?.[0];

    if (file) {
      this.handleFile(file);
    }
  }

  private handleFile(file: File) {
    if (!this.acceptedTypes.includes(file.type)) {
      this.toast.showError('Only PNG, JPG or GIF images are allowed.');
      return;
    }

    if (file.size > this.maxFileSize) {
      this.toast.showError('That file is larger than 10MB.');
      return;
    }

    this.selectedFile = file;
    this.selectedFileName = file.name;
  }

  removeFile() {
    this.selectedFile = null;
    this.selectedFileName = '';
  }

  /* --------------------------- Submitting ------------------------- */

  submitFeedback() {
    if (!this.rating) {
      this.toast.showError('Please rate your experience before submitting.');
      return;
    }

    if (!this.selectedCategory) {
      this.toast.showError('Please choose a feedback type.');
      return;
    }

    this.isSubmitting = true;

    const formData = new FormData();

    formData.append('feedback_type', this.selectedCategory);
    formData.append('rating', this.rating.toString());
    formData.append('message', this.message);

    if (this.selectedFile) {
      formData.append('screenshot', this.selectedFile);
    }

    this.commonService.post('/user/portal-feedback/', formData).subscribe({
      next: () => {
        this.toast.showSuccess('Feedback submitted successfully!');

        this.resetForm();
        this.closeModal.emit();
        this.isSubmitting = false;
      },
      error: (err) => {
        this.toast.showError('Error: Failed to submit feedback.');

        console.error(err);
        this.isSubmitting = false;
      },
    });
  }

  resetForm() {
    this.rating = 0;
    this.hoverRating = 0;
    this.message = '';
    this.selectedCategory = '';
    this.selectedFile = null;
    this.selectedFileName = '';
  }
}
